import React, { useState } from 'react';
import { Calculator, Settings, Users, Plus, UserPlus, LogOut, Copy } from 'lucide-react';
import { useApp } from '../context/AppContext';
import SettingsModal from './SettingsModal';
import SessionModal from './SessionModal';

const Header: React.FC = () => {
  const { state, dispatch } = useApp();
  const [showSettings, setShowSettings] = useState(false);
  const [showSessionModal, setShowSessionModal] = useState(false);
  const [sessionModalMode, setSessionModalMode] = useState<'create' | 'join'>('create');


  const handleCreateSession = () => {
    setSessionModalMode('create');
    setShowSessionModal(true);
  };

  const handleJoinSession = () => {
    setSessionModalMode('join');
    setShowSessionModal(true);
  };

  const handleLeaveSession = async () => {
    if (window.confirm('Are you sure you want to leave the current session?')) {
      try {
        if (state.currentSession && state.currentUser.id) {
          // Import sessionService dynamically to avoid circular imports
          const { sessionService } = await import('../services/sessionService');
          await sessionService.leaveSession(state.currentSession.id, state.currentUser.id);
        }
        dispatch({ type: 'LEAVE_SESSION' });
      } catch (error) {
        console.error('Failed to leave session:', error);
        alert('Failed to leave session. Please try again.');
      }
    }
  };

  return (
    <header className="bg-dark-800 border-b border-dark-700 px-4 sm:px-6 py-3 sm:py-4 shadow-lg">
      <div className="flex items-center justify-between">
        {/* Logo and Title */}
        <div className="flex items-center space-x-3">
          <div className="flex items-center space-x-2">
            <Calculator className="h-7 w-7 sm:h-8 sm:w-8 text-accent-500" />
            <div>
              <h1 className="text-xl sm:text-2xl font-bold text-white">DeCalculator</h1>
              <p className="text-xs text-dark-400 hidden sm:block">Collaborative Calculator</p>
            </div>
          </div>
        </div>

        {/* Controls */}
        <div className="flex items-center space-x-2 sm:space-x-4">
          {/* Session Management */}
          {!state.isMultiplayerMode ? (
            <div className="flex items-center space-x-2">
              <button
                onClick={handleCreateSession}
                className="flex items-center space-x-2 px-3 py-2 bg-accent-600 hover:bg-accent-700 text-white rounded-lg transition-all duration-200 shadow-md"
              >
                <Plus className="h-4 w-4" />
                <span className="text-sm font-medium hidden sm:inline">Create Session</span>
              </button>
              <button
                onClick={handleJoinSession}
                className="flex items-center space-x-2 px-3 py-2 bg-success-600 hover:bg-success-700 text-white rounded-lg transition-all duration-200 shadow-md"
              >
                <UserPlus className="h-4 w-4" />
                <span className="text-sm font-medium hidden sm:inline">Join Session</span>
              </button>
            </div>
          ) : (
            <div className="flex items-center space-x-2">
              <button
                onClick={handleLeaveSession}
                className="flex items-center space-x-2 px-3 py-2 bg-danger-600 hover:bg-danger-700 text-white rounded-lg transition-all duration-200 shadow-md"
              >
                <LogOut className="h-4 w-4" />
                <span className="text-sm font-medium hidden sm:inline">Leave Session</span>
              </button>
            </div>
          )}

          {/* Session Status */}
          {state.currentSession && (
            <div className="flex items-center space-x-2">
              <div className="flex items-center space-x-2 px-3 py-2 bg-accent-600 text-white rounded-lg">
                <Users className="h-4 w-4" />
                <span className="text-sm font-medium hidden sm:inline">
                  {state.currentSession.name}
                </span>
              </div>
              <div 
                className="flex items-center space-x-2 px-3 py-2 bg-dark-600 text-white rounded-lg cursor-pointer hover:bg-dark-700 transition-all duration-200"
                onClick={() => {
                  if (state.currentSession) {
                    navigator.clipboard.writeText(state.currentSession.id);
                    alert('Session ID copied to clipboard!');
                  }
                }}
                title="Click to copy Session ID"
              >
                <span className="text-xs text-dark-300">ID:</span>
                <span className="text-sm font-mono font-bold">
                  {state.currentSession?.id}
                </span>
                <Copy className="h-3 w-3" />
              </div>
            </div>
          )}

          {/* Online Users */}
          <div className="flex items-center space-x-2">
            <div className="flex -space-x-2">
              {(state.sessionUsers || []).map((user) => (
                <div
                  key={user.id}
                  className={`relative w-8 h-8 rounded-full border-2 border-dark-800 flex items-center justify-center text-sm font-semibold transition-all duration-200 ${
                    user.isOnline ? 'ring-2 ring-success-500' : 'opacity-50'
                  }`}
                  style={{ backgroundColor: user.color }}
                  title={`${user.name}${user.isAnonymous ? ' (Anonymous)' : ''} - ${user.isOnline ? 'Online' : 'Offline'}`}
                >
                  {user.avatar || (user.name ? user.name.charAt(0).toUpperCase() : '?')}
                  {user.isOnline && (
                    <div className="absolute -bottom-1 -right-1 w-3 h-3 bg-success-500 rounded-full border-2 border-dark-800"></div>
                  )}
                  {user.isAnonymous && (
                    <div className="absolute -top-1 -right-1 w-3 h-3 bg-warning-500 rounded-full border-2 border-dark-800 flex items-center justify-center">
                      <span className="text-xs text-white">?</span>
                    </div>
                  )}
                </div>
              ))}
            </div>
            <div className="hidden sm:block">
              <span className="text-sm text-dark-400">
                {state.sessionUsers.filter(u => u.isOnline).length} online
              </span>
            </div>
          </div>

          {/* Settings */}
          <button 
            onClick={() => setShowSettings(true)}
            className="p-2 text-dark-400 hover:text-white transition-colors rounded-lg hover:bg-dark-700"
          >
            <Settings className="h-5 w-5" />
          </button>
        </div>
      </div>

      {/* Modals */}
      <SettingsModal isOpen={showSettings} onClose={() => setShowSettings(false)} />
      <SessionModal 
        isOpen={showSessionModal} 
        onClose={() => setShowSessionModal(false)} 
        mode={sessionModalMode}
      />
    </header>
  );
};

export default Header;
